import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { sprintf } from 'sprintf-js';
import styled, { css } from 'styled-components';

import { Box } from 'components/Box';
import { TrackedPrimaryButtonLink } from 'components/Button';
import { Icon, IconName } from 'components/Icon';
import { LanguageLinks } from 'components/LanguageLinks';
import { RouterLink, TrackedLink } from 'components/Link';
import { IdealistLogo } from 'components/Logo/IdealistLogo';
import { BodySmall } from 'components/Text/BodySmall';
import { H5 } from 'components/Text/H5';
import { mainSalariesRouteGroup } from 'modules/main/main.routes';
import {
  colors,
  gutterWidth,
  maxWidth,
  textStyles,
  transition,
} from 'theme/theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE } from 'utils/constants/general/listingTypePluralHumanNameMapTitle';
import { getCurrentYear } from 'utils/date';
import { getHomeUrl } from 'utils/searchHomePages';

const linkStyles = css`
  display: block;
  color: ${colors.lightContentGrey};
  ${textStyles.bodySmall}
  &, * {
    line-height: 1.25;
  }
`;
const FooterLink = styled(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ({ className, key, href, eventName, eventProperties, children }: any) =>
    typeof href === 'string' ? (
      <TrackedLink
        className={className}
        key={key}
        href={href}
        eventName={eventName}
        eventProperties={eventProperties}
      >
        {children}
      </TrackedLink>
    ) : (
      <RouterLink
        className={className}
        key={key}
        to={href}
        onClick={() => {
          trackClicked(eventName, eventProperties);
        }}
      >
        {children}
      </RouterLink>
    ),
)`
  margin-right: 8px;
  margin-top: 1em;
  ${linkStyles}
`;
const SocialLink = styled(TrackedLink)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  margin: 0 8px;
  border-radius: 50%;
  background: ${colors.lightContentGrey};
  transition: all ${transition};
  &:hover,
  &:focus {
    background: ${colors.hoverBlue};
  }
`;

type FooterLinkItem = {
  href:
    | string
    | {
        pathname: string;
        state?: Record<string, unknown>;
      };
  page: string;
  locales?: Array<string>;
};
type FooterLinkSection = {
  title: string;
  links: Array<FooterLinkItem>;
};
const NAV_MAP = {
  ...LISTING_TYPE_PLURAL_HUMAN_NAME_MAP_TITLE,
  HOME: 'Idealist.org',
  GRAD_PROGRAM: getText('Grad Schools'),
  QUEUES: getText('Queues'),
  REPORTS: getText('Reports'),
  CREATE_PAYMENT: getText('Create Payment'),
  GRAD_FAIRS: getText('Grad Fairs'),
  EMAIL_PROMO: getText('Email Promos'),
  CMS: getText('CMS'),
};

function FooterLinks() {
  const { pathname } = useLocation();

  const footerLinks: Array<FooterLinkSection> = useMemo(
    () => [
      {
        title: getText('Discover'),
        links: ['JOB', 'INTERNSHIP', 'ORG', 'VOLOP', 'GRAD_PROGRAM']
          // @ts-expect-error TS(2345): Argument of type '(listingType: DefaultSearchType)... Remove this comment to see the full error message
          .map((listingType: DefaultSearchType) => ({
            href: getHomeUrl(
              listingType,
              listingType === 'GRAD_PROGRAM' ? { q: '' } : undefined,
            ),
            // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            page: NAV_MAP[listingType] || '',
          }))
          .concat([
            {
              href: mainSalariesRouteGroup.fullPath,
              page: getText('Nonprofit Salary Explorer'),
            },
            {
              href: getText('https://www.idealist.org/en/sitemap'),
              page: getText('Site Map'),
            },
          ]),
      },
      {
        title: getText('Post a Listing'),
        links: [
          {
            href: {
              pathname: '/post-a-listing',
              state: {
                from: pathname,
              },
            },
            page: getText('Post a Job'),
          },
          {
            href: getText('/organizations/create'),
            page: getText('Sign Up'),
          },
          {
            href: getText('/en/about/contact'),
            page: getText('Contact Us'),
          },
          {
            href: getText('/organizations'),
            page: getText('For Employers'),
          },
          {
            href: getText('/grad-schools/exhibit'),
            page: getText('For Grad Schools'),
          },
        ],
      },
      {
        title: getText('Resources'),
        links: [
          {
            href: getText(
              'https://www.idealist.org/en/jobs/idealist-resources',
            ),
            page: getText('Job Seeker Tools'),
            locales: ['en'],
          },
          {
            href: getText('/en/careers'),
            page: getText('Career Advice'),
            locales: ['en', 'pt'],
          },
          {
            href: getText('/grad-schools?pq='),
            page: getText('Grad Resources'),
            locales: ['en'],
          },
          {
            href: getText('/en/days/blog'),
            page: getText('Ideas for Action'),
            locales: ['en', 'pt'],
          },
          {
            href: getText('http://blogidealistas.org/'),
            page: getText('Blog Idealistas'),
            locales: ['es'],
          },
          {
            href: getText('/en/days/logos-new'),
            page: getText('Logo & Design'),
          },
          {
            href: 'https://www.idealist.org/en/newsletter',
            page: 'Newsletter',
            locales: ['en'],
          },
        ],
      },
      {
        title: getText('Learn More'),
        links: [
          {
            href: getText('/en/about'),
            page: getText('About Us'),
          },
          {
            href: getText('/en/about/our-team'),
            page: getText('Our Team'),
          },
          {
            href: getText('/en/help'),
            page: getText('Help & FAQ'),
          },
          {
            href: getText('/en/about/contact'),
            page: getText('Contact Us'),
          },
          {
            href: getText('/en/terms-of-service'),
            page: getText('Terms of Service'),
          },
          {
            href: getText('/en/privacy-policy'),
            page: getText('Privacy Policy'),
          },
        ],
      },
    ],
    [pathname],
  );
  return (
    <>
      {footerLinks.map((section, index) => (
        <Box
          width={[1, 1 / 2, 1 / 4]}
          flex="0 0 auto"
          key={section.title}
          mt={index > 0 ? [32, null, 0] : undefined}
          display={index <= 1 ? ['none', null, 'block'] : 'block'}
        >
          <H5 mb={24}>{section.title}</H5>
          {section.links
            .filter(
              ({ locales }) => !locales || locales.includes(CURRENT_LOCALE),
            )
            .map(({ href, page }) => (
              <FooterLink
                href={href}
                key={page}
                eventName="Resource Page"
                eventProperties={{
                  page,
                }}
              >
                {page}
              </FooterLink>
            ))}
        </Box>
      ))}
    </>
  );
}

const socialLinks: { href: string; eventName: string; name: IconName }[] = [
  {
    href: getText('https://www.tiktok.com/@joinidealist'),
    eventName: 'Follow Idealist',
    name: 'social/tiktok',
  },
  {
    href: getText('https://www.instagram.com/joinidealist/'),
    eventName: 'Follow Idealist',
    name: 'social/instagram',
  },
  {
    href: getText('https://www.facebook.com/Idealist'),
    eventName: 'Follow Idealist',
    name: 'social/facebook',
  },
  {
    href: getText('https://twitter.com/idealist'),
    eventName: 'Follow Idealist',
    name: 'social/twitter',
  },
];

function SocialLinks() {
  return (
    <Box display="flex" justifyContent="start">
      {socialLinks.map(({ href, eventName, name }) => (
        <SocialLink
          href={href}
          key={name}
          eventName={eventName}
          aria-label={eventName}
        >
          <Icon name={name} size={20} colorName="white" />
        </SocialLink>
      ))}
    </Box>
  );
}

function MissionStatement() {
  return (
    <BodySmall
      color={colors.lightContentGrey}
      textAlign={['center', null, null, 'left']}
      lineHeight={1.5}
    >
      {
        /* Mission statement under logo */
        getText(`Working with others, in a spirit of generosity and mutual respect, we want to
      help build a world where all people can lead free and dignified lives.`)
      }
    </BodySmall>
  );
}

type Props = {
  urlByLocale: Record<UserLocale, string | undefined> | undefined;
  inPageHeader?: boolean;
  hideDivider?: boolean;
};

export function PageFooter({ urlByLocale, inPageHeader, hideDivider }: Props) {
  return (
    <Box
      component="footer"
      data-qa-id="footer"
      mx="auto"
      px={gutterWidth}
      py={gutterWidth}
      maxWidth={maxWidth[3]}
      backgroundColor={inPageHeader ? colors.inputGrey : colors.white}
      borderTop={hideDivider ? null : `1px solid ${colors.selectionGrey}`}
    >
      <Box display="flex" flexDirection="column">
        <Box
          display={['flex', 'flex', 'flex', 'none']}
          flexDirection="column"
          alignItems="center"
        >
          <Box mb={4} width={8 / 10}>
            <MissionStatement />
          </Box>

          <Box mb={4}>
            <TrackedPrimaryButtonLink
              href={getText('/donate')}
              $size="large"
              eventName="Donate - Footer"
            >
              {getText('Donate to Idealist')}
            </TrackedPrimaryButtonLink>
          </Box>
        </Box>
        <Box display={['none', 'none', 'none', 'flex']} mb={5}>
          <Box
            width={3 / 10}
            mr={5}
            pr={5}
            borderRight={`1px solid ${colors.selectionGrey}`}
          >
            <TrackedLink eventName="Footer Logo" href="/">
              <div style={{ lineHeight: 0 }}>
                <IdealistLogo withText height={56} />
              </div>
            </TrackedLink>
            <Box mb={4} mt={4}>
              <MissionStatement />
            </Box>
            <TrackedPrimaryButtonLink
              href={getText('/donate')}
              $size="large"
              eventName="Donate - Footer"
            >
              {getText('Donate to Idealist')}
            </TrackedPrimaryButtonLink>
          </Box>
          <Box display="flex" width={7 / 10}>
            <FooterLinks />
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection={['column', 'column', 'column', 'row']}
          justifyContent="space-between"
          alignItems="center"
          width={1}
        >
          <Box
            display="flex"
            justifyContent={['center', 'center', 'center', 'start']}
            order={1}
            width={[1, 1, 1, 1 / 3]}
            mb={[5, 5, 5, 0]}
          >
            <SocialLinks />
          </Box>
          <Box
            display="flex"
            justifyContent={['center', 'center', 'center', 'center']}
            order={[3, 3, 3, 2]}
            width={[1, 1, 1, 1 / 3]}
          >
            <BodySmall color={colors.lightContentGrey}>
              {sprintf(getText('Copyright © %(year)s Idealist.org'), {
                year: getCurrentYear(),
              })}
            </BodySmall>
          </Box>

          <Box
            display="flex"
            justifyContent={['center', 'center', 'center', 'end']}
            order={[2, 2, 2, 3]}
            width={[1, 1, 1, 1 / 3]}
            mb={[2, 2, 2, 0]}
          >
            <LanguageLinks urlByLocale={urlByLocale} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
